import { useState } from 'react';
import { Lightning, Warning, CheckCircle, Eye } from '@phosphor-icons/react';
import { useAutoVincular } from '@/hooks/useBootstrap';
import type { AutoVincularResult } from '@/types';

// ============================================================
// Panel del auto-vínculo (B4). Dispara el endpoint que resuelve los grupos
// donde no hay juicio humano que agregar: un solo candidato, confianza Alta,
// código presente y sin trámite vigente (re-trámite → siempre humano, 9.6).
//
// El flujo es de DOS pasos a propósito. Primero el ensayo (`dryRun`), que
// CUENTA sin escribir; recién con el número a la vista aparece el botón que
// escribe trámites de verdad, y ese pide un segundo clic. Escribir ~212
// trámites en AdelanteDB no puede ser un clic distraído.
// ============================================================

// Lo que QUEDA en la bandeja = el desglose de omitidos + los elegibles que no se
// procesaron por el tope. Sumar solo los omitidos mentiría con `limite` puesto:
// con 199 elegibles y tope 10, quedan 59 + 189, no 59.
function sumarPendientes(r: AutoVincularResult) {
  const omitidos = Object.values(r.omitidosPorMotivo).reduce((a, b) => a + b, 0);
  return omitidos + Math.max(0, r.elegibles - r.procesados);
}

function Numerito({ valor, label, tono }: { valor: number; label: string; tono: 'verde' | 'gris' }) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-brand-gray-200 bg-white px-4 py-2">
      <span className={`text-2xl font-bold ${tono === 'verde' ? 'text-brand-green-200' : 'text-brand-gray-500'}`}>
        {valor}
      </span>
      <span className="text-[11px] text-brand-gray-400">{label}</span>
    </div>
  );
}

export function AutoVincularPanel({ onIrAVerificar }: { onIrAVerificar: () => void }) {
  const auto = useAutoVincular();
  // El resultado del ensayo se guarda aparte: la corrida real reemplaza
  // `auto.data`, y queremos seguir mostrando contra qué número se corrió.
  const [ensayo, setEnsayo] = useState<AutoVincularResult | null>(null);
  // Segundo clic de la corrida real.
  const [armado, setArmado] = useState(false);
  // Tope opcional: una corrida chica antes de la grande.
  const [limite, setLimite] = useState('');

  const real = auto.data && !auto.data.dryRun ? auto.data : null;
  const lim = Math.max(0, parseInt(limite, 10) || 0);

  function correrEnsayo() {
    setArmado(false);
    auto.mutate({ dryRun: true }, { onSuccess: r => setEnsayo(r) });
  }
  function correrDeVerdad() {
    setArmado(false);
    auto.mutate({ dryRun: false, limite: lim });
  }

  return (
    <section className="mb-6 rounded-2xl border border-brand-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <Lightning size={20} weight="fill" className="text-brand-yellow" />
        <h2 className="text-base font-semibold text-brand-gray-500">Auto-vínculo con salvaguarda</h2>
      </div>
      <p className="mb-3 max-w-3xl text-sm text-brand-gray-400">
        Vincula solo los códigos donde no hay nada que decidir: <strong>un único candidato</strong> de
        confianza <strong>Alta</strong>, código CFIA/APC presente y un lote que no tenga otros códigos.
        Deja para el humano los ambiguos, los que no tienen candidato, los numéricos a verificar, los
        posibles re-trámites y los <strong>lotes con varios códigos</strong> (esos se resuelven de una sola
        vez en su tarjeta, abajo). Lo auto-vinculado queda marcado y pasa a <strong>Verificar</strong>, que
        es leer y confirmar — no decidir.
      </p>

      {/* Paso 1 — ensayo */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={correrEnsayo}
          disabled={auto.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-brand-gray-500 hover:bg-brand-gray-100 disabled:opacity-50"
        >
          <Eye size={16} weight="bold" />
          {auto.isPending ? 'Calculando…' : 'Ensayo: contar sin escribir'}
        </button>
        {ensayo && !real && (
          <span className="text-[11px] text-brand-gray-400">
            El ensayo no escribió nada en la base.
          </span>
        )}
      </div>

      {/* Resultado del ensayo + paso 2 (corrida real) */}
      {ensayo && !real && (
        <div className="mt-3 flex flex-col gap-3 rounded-xl bg-brand-bg p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Numerito valor={ensayo.gruposTotales} label="códigos en la bandeja" tono="gris" />
            <span className="text-brand-gray-300">→</span>
            <Numerito valor={ensayo.elegibles} label="se auto-vinculan" tono="verde" />
            <span className="text-brand-gray-300">+</span>
            <Numerito valor={sumarPendientes(ensayo)} label="decisiones humanas" tono="gris" />
          </div>

          {/* Desglose de lo que queda: el humano ve EN QUÉ va a trabajar. */}
          {Object.keys(ensayo.omitidosPorMotivo).length > 0 && (
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-gray-400">
                Qué queda para el humano
              </span>
              {Object.entries(ensayo.omitidosPorMotivo)
                .sort((a, b) => b[1] - a[1])
                .map(([motivo, n]) => (
                  <div key={motivo} className="flex items-baseline gap-2 text-xs text-brand-gray-400">
                    <span className="w-8 shrink-0 text-right font-mono font-semibold text-brand-gray-500">{n}</span>
                    <span>{motivo}</span>
                  </div>
                ))}
            </div>
          )}

          {ensayo.elegibles === 0 ? (
            <p className="text-sm italic text-brand-gray-400">
              No hay nada auto-vinculable: todo lo que queda necesita ojo humano.
            </p>
          ) : !armado ? (
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-0.5">
                <span className="text-[11px] text-brand-gray-400">Tope (opcional, 0 = todos)</span>
                <input
                  value={limite}
                  onChange={e => setLimite(e.target.value.replace(/\D/g, ''))}
                  placeholder="0"
                  inputMode="numeric"
                  className="w-24 rounded border border-brand-gray-300 px-2 py-1 text-sm outline-none focus:border-brand-green-200"
                />
              </label>
              <button
                type="button"
                onClick={() => setArmado(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-green-100 px-3 py-1.5 text-sm font-semibold text-black hover:bg-brand-green-200"
              >
                <Lightning size={15} weight="bold" />
                Auto-vincular {lim > 0 ? `${Math.min(lim, ensayo.elegibles)} de ${ensayo.elegibles}` : `los ${ensayo.elegibles}`}
              </button>
            </div>
          ) : (
            // Segundo clic: esto escribe trámites reales en AdelanteDB.
            <div className="flex flex-col gap-2 rounded-lg bg-brand-yellow/15 p-2.5">
              <div className="flex items-start gap-1.5 text-xs text-brand-gray-500">
                <Warning size={15} weight="bold" className="mt-0.5 shrink-0 text-brand-yellow" />
                <span>
                  Esto crea <strong>{lim > 0 ? Math.min(lim, ensayo.elegibles) : ensayo.elegibles} trámites
                  reales</strong> en AdelanteDB y mueve sus correos a <em>Aplicado</em>. Lo creado queda en
                  Verificar; si alguno salió mal, se rechaza desde ahí y vuelve a la bandeja.
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={correrDeVerdad}
                  disabled={auto.isPending}
                  className="rounded-lg bg-brand-green-100 px-3 py-1.5 text-sm font-semibold text-black hover:bg-brand-green-200 disabled:opacity-50"
                >
                  {auto.isPending ? 'Vinculando…' : 'Sí, auto-vincular'}
                </button>
                <button
                  type="button"
                  onClick={() => setArmado(false)}
                  className="rounded-lg border border-brand-gray-300 bg-white px-3 py-1.5 text-sm text-brand-gray-500 hover:bg-brand-gray-100"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Resultado de la corrida real */}
      {real && (
        <div className="mt-3 flex flex-col gap-2 rounded-xl bg-brand-bg p-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-brand-gray-500">
            <CheckCircle size={18} weight="fill" className="text-brand-green-200" />
            {real.vinculados} {real.vinculados === 1 ? 'trámite' : 'trámites'} auto-vinculados
            {real.fallidos > 0 && <span className="text-brand-gray-400">· {real.fallidos} sin vincular</span>}
          </div>
          <p className="text-xs text-brand-gray-400">
            Quedan <strong>{sumarPendientes(real)}</strong> grupos en la bandeja
            {real.elegibles > real.procesados && (
              <> — de esos, <strong>{real.elegibles - real.procesados}</strong> son auto-vinculables
              y quedaron fuera por el tope: volvé a correr el ensayo para seguir</>
            )}.
          </p>
          <button
            type="button"
            onClick={onIrAVerificar}
            className="self-start text-xs font-semibold text-brand-gray-500 underline hover:text-brand-gray-400"
          >
            Ir a verificar lo auto-vinculado →
          </button>
          {real.errores.length > 0 && (
            <div className="flex flex-col gap-1 rounded-lg bg-brand-red-100/15 p-2.5 text-[11px] text-brand-red-200">
              <span className="font-semibold">Grupos que fallaron:</span>
              {real.errores.map((e, i) => <span key={i} className="font-mono">{e}</span>)}
            </div>
          )}
        </div>
      )}

      {auto.isError && (
        <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-brand-red-100/15 px-2.5 py-2 text-xs text-brand-red-200">
          <Warning size={15} weight="bold" className="mt-0.5 shrink-0" />
          {(auto.error as Error)?.message ?? 'Error al correr el auto-vínculo'}
        </div>
      )}
    </section>
  );
}
